import React, { useEffect, useState } from 'react';
import { Accessibility, X, Sun, Contrast, Type, MousePointer2, Link2, Info, RotateCcw } from 'lucide-react';

const STORAGE_KEY = 'apsa11y';

const defaultSettings = {
  fontScale: 1,         // 1.0, 1.15, 1.3, 1.5
  letterSpacing: 0,     // 0, 1, 2 (em * 0.05)
  highContrast: false,
  lightMode: false,     // bright background, dark text
  grayscale: false,
  highlightLinks: false,
  bigCursor: false,
  focusRings: false,
};

const AccessibilityWidget = () => {
  const [open, setOpen] = useState(false);
  const [showStatement, setShowStatement] = useState(false);
  const [settings, setSettings] = useState(defaultSettings);

  // Load saved settings (user a11y preferences only — no PII)
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) setSettings({ ...defaultSettings, ...JSON.parse(raw) });
    } catch (err) {
      console.warn('[a11y] could not read saved settings:', err);
    }
  }, []);

  // Apply settings to <html>
  useEffect(() => {
    const html = document.documentElement;
    html.style.fontSize = `${settings.fontScale * 100}%`;
    html.style.letterSpacing = `${settings.letterSpacing * 0.05}em`;
    html.classList.toggle('a11y-high-contrast', settings.highContrast);
    html.classList.toggle('a11y-light-mode', settings.lightMode);
    html.classList.toggle('a11y-grayscale', settings.grayscale);
    html.classList.toggle('a11y-highlight-links', settings.highlightLinks);
    html.classList.toggle('a11y-big-cursor', settings.bigCursor);
    html.classList.toggle('a11y-focus-rings', settings.focusRings);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch (err) {
      console.warn('[a11y] could not persist settings:', err);
    }
  }, [settings]);

  const toggle = (key) => setSettings((s) => ({ ...s, [key]: !s[key] }));
  const bumpFont = () =>
    setSettings((s) => {
      const scales = [1, 1.15, 1.3, 1.5];
      const idx = scales.indexOf(s.fontScale);
      return { ...s, fontScale: scales[(idx + 1) % scales.length] };
    });
  const bumpSpacing = () =>
    setSettings((s) => ({ ...s, letterSpacing: (s.letterSpacing + 1) % 3 }));
  const reset = () => setSettings(defaultSettings);

  const toggles = [
    { key: 'highContrast', label: 'High Contrast', Icon: Contrast },
    { key: 'lightMode', label: 'Light Mode', Icon: Sun },
    { key: 'grayscale', label: 'Grayscale', Icon: Contrast },
    { key: 'highlightLinks', label: 'Highlight Links', Icon: Link2 },
    { key: 'bigCursor', label: 'Big Cursor', Icon: MousePointer2 },
    { key: 'focusRings', label: 'Focus Rings', Icon: Type },
  ];

  return (
    <>
      {/* Floating button (bottom left) */}
      <button
        type="button"
        aria-label="Open accessibility menu"
        onClick={() => setOpen(true)}
        className="fixed bottom-5 left-5 z-[60] w-14 h-14 rounded-full bg-[#1e4fa3] text-white flex items-center justify-center shadow-xl hover:bg-[#183d80] hover:scale-105 transition-all"
      >
        <Accessibility className="w-7 h-7" />
      </button>

      {/* Backdrop */}
      {open && (
        <button
          type="button"
          aria-label="Close accessibility menu"
          className="fixed inset-0 z-[70] bg-black/40"
          onClick={() => setOpen(false)}
        />
      )}

      {/* Panel */}
      <aside
        role="dialog"
        aria-label="Accessibility tools"
        className={`fixed left-0 top-0 bottom-0 w-[340px] max-w-[90vw] bg-white z-[80] shadow-2xl transform transition-transform duration-300 flex flex-col ${
          open ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <header className="flex items-center justify-between px-5 py-4 bg-[#1e4fa3] text-white">
          <div className="flex items-center gap-2">
            <Accessibility className="w-5 h-5" />
            <h2 className="font-semibold text-[15px]">Accessibility Tools</h2>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={() => setOpen(false)}
            className="w-8 h-8 flex items-center justify-center hover:bg-white/20 rounded"
          >
            <X className="w-5 h-5" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Content sizing */}
          <section>
            <h3 className="text-[12px] font-bold uppercase tracking-wider text-[#0b1e4d] mb-3">
              Content
            </h3>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={bumpFont}
                className="flex flex-col items-center justify-center gap-1 border border-gray-200 hover:border-[#1e4fa3] rounded px-3 py-3 text-[#0b1e4d] transition-colors"
              >
                <Type className="w-5 h-5 text-[#1e4fa3]" />
                <span className="text-[12px] font-semibold">
                  Text Size ({Math.round(settings.fontScale * 100)}%)
                </span>
              </button>
              <button
                type="button"
                onClick={bumpSpacing}
                className="flex flex-col items-center justify-center gap-1 border border-gray-200 hover:border-[#1e4fa3] rounded px-3 py-3 text-[#0b1e4d] transition-colors"
              >
                <Type className="w-5 h-5 text-[#1e4fa3]" />
                <span className="text-[12px] font-semibold">
                  Spacing (Lv {settings.letterSpacing})
                </span>
              </button>
            </div>
          </section>

          {/* Toggles */}
          <section>
            <h3 className="text-[12px] font-bold uppercase tracking-wider text-[#0b1e4d] mb-3">
              Visual & Navigation
            </h3>
            <div className="grid grid-cols-2 gap-2">
              {toggles.map(({ key, label, Icon }) => {
                const active = settings[key];
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => toggle(key)}
                    aria-pressed={active}
                    className={`flex flex-col items-center justify-center gap-1 rounded px-3 py-3 border transition-colors ${
                      active
                        ? 'bg-[#1e4fa3] text-white border-[#1e4fa3]'
                        : 'bg-white text-[#0b1e4d] border-gray-200 hover:border-[#1e4fa3]'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="text-[12px] font-semibold text-center">{label}</span>
                  </button>
                );
              })}
            </div>
          </section>

          {/* Actions */}
          <section className="flex flex-col gap-2">
            <button
              type="button"
              onClick={reset}
              className="flex items-center justify-center gap-2 bg-gray-100 hover:bg-gray-200 text-[#0b1e4d] font-semibold text-[13px] px-4 py-2.5 rounded transition-colors"
            >
              <RotateCcw className="w-4 h-4" /> Reset Settings
            </button>
            <button
              type="button"
              onClick={() => setShowStatement(true)}
              className="flex items-center justify-center gap-2 bg-[#0b1e4d] hover:bg-[#1e4fa3] text-white font-semibold text-[13px] px-4 py-2.5 rounded transition-colors"
            >
              <Info className="w-4 h-4" /> Accessibility Statement
            </button>
          </section>
        </div>
        <footer className="px-5 py-3 border-t border-gray-200 text-center text-[11px] text-gray-500">
          WCAG 2.1 Level AA focused
        </footer>
      </aside>

      {/* Accessibility statement modal */}
      {showStatement && (
        <AccessibilityStatementModal onClose={() => setShowStatement(false)} />
      )}
    </>
  );
};

const AccessibilityStatementModal = ({ onClose }) => (
  <div
    role="dialog"
    aria-label="Accessibility Statement"
    className="fixed inset-0 z-[90] bg-black/60 flex items-center justify-center p-4"
  >
    <div className="bg-white max-w-2xl w-full max-h-[85vh] rounded-sm shadow-2xl flex flex-col">
      <header className="flex items-center justify-between px-6 py-4 bg-[#0b1e4d] text-white">
        <h2 className="font-serif text-xl font-bold">Website Accessibility Statement</h2>
        <button
          type="button"
          aria-label="Close statement"
          onClick={onClose}
          className="w-8 h-8 flex items-center justify-center hover:bg-white/20 rounded"
        >
          <X className="w-5 h-5" />
        </button>
      </header>
      <div className="overflow-y-auto px-6 py-5 text-[14px] text-gray-800 leading-relaxed space-y-4">
        <p className="text-xs text-gray-500">Last Updated: February 27, 2026</p>

        <div>
          <h3 className="font-bold text-[#0b1e4d] mb-1">Our Commitment to Accessibility</h3>
          <p>
            We are committed to ensuring digital accessibility for people with
            disabilities. We are continually improving the user experience for
            everyone and applying relevant accessibility standards to enhance
            usability and inclusivity. Our goal is to provide a website that is
            accessible to the widest possible audience, regardless of technology
            or ability.
          </p>
        </div>

        <div>
          <h3 className="font-bold text-[#0b1e4d] mb-1">Accessibility Standards</h3>
          <p>
            We strive to conform to the Web Content Accessibility Guidelines
            (WCAG) 2.1 Level AA, which are widely recognized as the industry
            standard for website accessibility. Accessibility is an ongoing
            process, and we are actively working toward improving the
            accessibility and usability of our website.
          </p>
        </div>

        <div>
          <h3 className="font-bold text-[#0b1e4d] mb-1">Accessibility Technology &amp; Tools</h3>
          <p>
            To support our accessibility efforts, we have deployed
            industry-standard accessibility technology designed to enhance
            usability and assist visitors with disabilities. These tools may
            provide features such as:
          </p>
          <ul className="list-disc pl-5 mt-1 space-y-1">
            <li>Screen reader enhancements</li>
            <li>Keyboard navigation improvements</li>
            <li>Color contrast adjustments</li>
            <li>Text size and spacing controls</li>
            <li>Focus indicators</li>
            <li>Additional accessibility interface options</li>
          </ul>
          <p className="mt-2">
            This technology helps us improve accessibility while we continue
            broader auditing, remediation, and development efforts.
          </p>
        </div>

        <div>
          <h3 className="font-bold text-[#0b1e4d] mb-1">Ongoing Improvement</h3>
          <p>Accessibility is an ongoing initiative. We are:</p>
          <ul className="list-disc pl-5 mt-1 space-y-1">
            <li>Reviewing site content and functionality regularly</li>
            <li>Working with developers and accessibility tools</li>
            <li>Identifying and remediating barriers</li>
            <li>Incorporating accessibility into future design and development</li>
          </ul>
          <p className="mt-2">
            We understand accessibility is a continuous journey and are
            committed to making improvements over time.
          </p>
        </div>

        <div>
          <h3 className="font-bold text-[#0b1e4d] mb-1">Third-Party Content</h3>
          <p>
            Some areas of the website may include third-party content, plugins,
            or integrations that are not fully controlled by us. While we
            encourage vendors to provide accessible content, we cannot guarantee
            accessibility of third-party features. However, we welcome feedback
            regarding any issues encountered.
          </p>
        </div>

        <div>
          <h3 className="font-bold text-[#0b1e4d] mb-1">Feedback &amp; Assistance</h3>
          <p>
            If you experience difficulty accessing any part of this website,
            need assistance, or would like to request content in an alternative
            format, please contact us.
          </p>
          <p className="mt-2">Please include:</p>
          <ul className="list-disc pl-5 mt-1 space-y-1">
            <li>The page URL</li>
            <li>Description of the issue</li>
            <li>Your preferred contact method</li>
          </ul>
          <p className="mt-2">
            Contact: Information on our{' '}
            <a
              href="https://american-pools.com/contact-us/"
              className="text-[#1e4fa3] underline"
            >
              Contact Us Page
            </a>
            . We aim to respond within a reasonable timeframe and will make
            reasonable efforts to provide accessible alternatives.
          </p>
        </div>

        <div>
          <h3 className="font-bold text-[#0b1e4d] mb-1">Good Faith Efforts</h3>
          <p>
            We are making good-faith efforts to comply with accessibility
            standards and improve usability for individuals with disabilities.
            Despite our efforts, some content may not yet fully meet the
            strictest accessibility standards due to the evolving nature of
            technology and ongoing updates. We appreciate your understanding as
            we continue improving accessibility.
          </p>
        </div>

        <div>
          <h3 className="font-bold text-[#0b1e4d] mb-1">Terms &amp; Conditions — Accessibility Clause</h3>
          <p>
            We are committed to providing an accessible website experience for
            all users and strive to follow recognized accessibility standards,
            including WCAG guidelines. We have implemented industry-standard
            accessibility tools and continue to review, update, and improve our
            website to enhance accessibility.
          </p>
          <p className="mt-2">
            Because accessibility is an ongoing process, some areas of the
            website may not yet fully conform to all accessibility standards.
            If you encounter accessibility barriers, please contact us so we
            can provide assistance, offer alternative access, and address the
            issue where feasible.
          </p>
          <p className="mt-2">
            Nothing in these Terms is intended to limit any rights available
            under applicable accessibility laws.
          </p>
        </div>
      </div>
      <footer className="px-6 py-3 border-t border-gray-200 flex justify-end">
        <button
          type="button"
          onClick={onClose}
          className="bg-[#1e4fa3] hover:bg-[#183d80] text-white font-semibold text-[13px] px-5 py-2 rounded transition-colors"
        >
          Close
        </button>
      </footer>
    </div>
  </div>
);

export default AccessibilityWidget;
